import { useMemo } from 'react'

import { Audiotrack } from '@mui/icons-material'
import { Tooltip, Typography } from '@mui/material'
import { AudioStream } from '@nativewaves/nw-cloud-sdk/content'
import { ColumnDef } from '@tanstack/react-table'
import { upperFirst } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { CopyId } from '@shared/components/Table/Fields'
import { StateVisualizer } from 'domains/Workspaces/Audio/Streams'
import { NoSrcPlaceholderWrapper } from 'domains/Workspaces/Events'
import { formatContentLifecycleDuration } from 'domains/Workspaces/Streams'

export const useColumns = () => {
  const { t } = useTranslation(['common', 'domain'])

  return useMemo<ColumnDef<AudioStream>[]>(
    () => [
      {
        accessorFn: (row) => ({ src: row.thumbnail?.url, alt: row.name || row.id }),
        id: '_thumbnail',
        header: () => null,
        meta: {
          sizing: {
            width: 96,
            px: 2,
          },
          align: 'center',
        },
        cell: () => (
          <NoSrcPlaceholderWrapper>
            <Audiotrack />
          </NoSrcPlaceholderWrapper>
        ),
      },
      {
        accessorFn: (row) => (row.name.length ? row.name : t('noNameProvided')),
        header: t('name'),
        meta: {
          sizing: {
            minWidth: 300,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorFn: (row) => (row.live ? 'live' : 'ended'),
        id: '_status',
        header: () => t('status'),
        meta: {
          sizing: {
            width: 100,
            px: 2,
          },
          align: 'center',
        },
        cell: ({ getValue }) => {
          const state = getValue<'live' | 'ended'>()

          return (
            <StateVisualizer state={state === 'live'} variant="text-sm">
              {upperFirst(state.toLowerCase())}
            </StateVisualizer>
          )
        },
      },
      {
        accessorKey: 'contentLifecyclePolicy',
        header: t('domain:Workspace.Audio.contentLifecycle'),
        meta: {
          sizing: {
            width: 180,
            px: 2,
          },
        },
        cell: ({ getValue }) => {
          const duration = formatContentLifecycleDuration(getValue<AudioStream['contentLifecyclePolicy']>().deleteAfter)

          const label = duration ? duration.label : t('domain:Workspace.Audio.contentLifecycleNoExpiry')

          const tooltipTitle = duration
            ? t('domain:Workspace.Audio.contentLifecycleDeletion', { time: duration.full })
            : t('domain:Workspace.Audio.contentLifecycleNoDeletion')

          return (
            <Tooltip title={tooltipTitle} placement="top">
              <Typography>{label}</Typography>
            </Tooltip>
          )
        },
      },
      {
        accessorKey: 'id',
        header: t('id'),
        meta: {
          sizing: {
            width: 200,
            px: 2,
          },
        },
        cell: ({ getValue }) => <CopyId id={getValue<string>()} />,
      },
    ],
    [t],
  )
}
